/**
 * Layer Utilities
 * Centralized layer name normalization and category routing.
 */

/**
 * Normalize layer name by removing prefix (c-, e-, p-).
 * "c-cctv" → "cctv", "e-elevator" → "elevator", "parking" → "parking"
 */
export function normalizeLayerName(layer: string): string {
  if (/^[cepls]-/i.test(layer)) {
    return layer.slice(2)
  }
  return layer
}

/**
 * Extract layer category prefix.
 * "c-cctv" → "c", "e-elevator" → "e", "parking" → null
 */
export function getLayerPrefix(layer: string): 'c' | 'e' | 'p' | null {
  const match = layer.match(/^([cep])-/i)
  return match ? (match[1].toLowerCase() as 'c' | 'e' | 'p') : null
}

// Direct type name → category mapping.
// 매뉴얼 placement (useObjectCreation) 으로 만든 객체의 `data.layer` 는
// (1) convention layer name (예: 'c-cctv', 'e-charger') 또는
// (2) `selectedType.name` (한글 displayName) 으로 떨어진다.
// 템플릿(parking.json) initialObjects 으로 만든 객체의 `data.type` 은 템플릿 키
// (예: 'lightCctv', 'carCharger') 다. 세 경우 모두 export bucket 으로 라우팅되도록
// 한글 displayName + 템플릿 키 + PascalCase 별칭을 모두 등록한다.
const TYPE_MAPPING: Record<string, string> = {
  // PascalCase 별칭 (legacy / external import)
  'CCTV': 'cctv',
  'Camera': 'cctv',
  'Zone': 'zone',
  'ParkingZone': 'zone',
  'Column': 'column',
  'Pillar': 'column',
  'ParkingSpot': 'parkingLocation',
  'ParkingLocation': 'parkingLocation',
  'Sensor': 'sensor',
  'Charger': 'charger',
  'EVCharger': 'charger',
  'CarCharger': 'carCharger',
  'GuideBoard': 'guideBoard',
  'Sign': 'guideBoard',
  'Elevator': 'elevator',
  'Lift': 'elevator',
  'EmergencyBell': 'emergencyBell',
  'Arrow': 'arrow',
  'OutLine': 'outLine',
  'InnerLine': 'innerLine',
  'Entrance': 'entrance',
  'OnePassReader': 'onePassReader',
  'OccupancyLight': 'occupancyLight',
  'Light': 'light',
  // 템플릿 키 (parking.json `objectTypes.*` 키 그대로)
  'lightCctv': 'cctv',
  'warningCctv': 'warningCctv',
  'guideBoard': 'guideBoard',
  'zone': 'zone',
  'parkingLocation': 'parkingLocation',
  'arrow': 'arrow',
  'column': 'column',
  'outLine': 'outLine',
  'innerLine': 'innerLine',
  'elevator': 'elevator',
  'entrance': 'entrance',
  'carCharger': 'carCharger',
  'emergencyBell': 'emergencyBell',
  'onePassReader': 'onePassReader',
  'lights': 'light',
  'occupancyLight': 'occupancyLight',
  // 한글 displayName — useObjectCreation 이 conventionLayer 가 없으면
  // selectedType.name (한글) 을 그대로 data.layer 에 박는다. 그 값으로
  // export bucket 까지 도달하도록 매핑.
  '조명 CCTV': 'cctv',
  '단방향 CCTV': 'warningCctv',
  'CCTV ': 'cctv',
  '안내판': 'guideBoard',
  '안내현황판': 'guideBoard',
  '현황판': 'guideBoard',
  '존': 'zone',
  '구역': 'zone',
  '주차 구역': 'parkingLocation',
  '주차구역': 'parkingLocation',
  '화살표': 'arrow',
  '기둥': 'column',
  '외곽선': 'outLine',
  '내부선': 'innerLine',
  '엘리베이터': 'elevator',
  '입구/출구': 'entrance',
  '전기 충전기': 'carCharger',
  '비상벨': 'emergencyBell',
  '원패스 리더기': 'onePassReader',
  '조명': 'light',
  '만공차등': 'occupancyLight',
}

// Layer prefix → category pattern matching.
// 매뉴얼 placement 용 canonical layer key (p-lightCctv/p-warningCctv/c-carCharger)
// 는 더 일반적인 패턴(p-cctv, e-charger) 보다 먼저 등록해 명확히 매칭되도록 한다.
const LAYER_PATTERNS: Array<[RegExp, string]> = [
  [/^p-lightcctv$/i, 'cctv'],
  [/^p-warningcctv$/i, 'warningCctv'],
  [/^p-light-cctv$/i, 'cctv'],
  [/^p-warning-cctv$/i, 'warningCctv'],
  [/^c-carcharger$/i, 'carCharger'],
  [/^c-cctv/i, 'cctv'],
  [/^c-camera/i, 'cctv'],
  // Zone-drawing 툴은 data.layer 를 ZONE_LAYER_NAME ('zones') 로 저장.
  // 기존 'ZONE' (uppercase) 으로 저장된 레거시 데이터도 함께 잡히도록
  // 'zone' / 'zones' 둘 다 case-insensitive 로 'zone' 카테고리에 매핑.
  // (TYPE_MAPPING 에 'Zone' 만 있어서 'ZONE' / 'zones' 가 unknown 으로
  //  떨어지던 버그 — JSON export 의 zones 버킷이 빈 채로 나오던 원인.)
  [/^zones?$/i, 'zone'],
  [/^e-zone/i, 'zone'],
  [/^e-parking.*zone/i, 'zone'],
  [/^e-pillar/i, 'column'],
  [/^e-column/i, 'column'],
  [/^p-parking/i, 'parkingLocation'],
  [/^c-sensor/i, 'sensor'],
  [/^e-charger/i, 'charger'],
  [/^e-carcharger/i, 'carCharger'],
  [/^p-guideboard/i, 'guideBoard'],
  [/^e-elevator/i, 'elevator'],
  [/^c-emergencybell/i, 'emergencyBell'],
  [/^e-arrow/i, 'arrow'],
  [/^e-outline/i, 'outLine'],
  [/^s-outline/i, 'outLine'],
  [/^e-innerline/i, 'innerLine'],
  [/^e-entrance/i, 'entrance'],
  [/^c-onepassreader/i, 'onePassReader'],
  [/^c-occupancylight/i, 'occupancyLight'],
  // 조명(light)의 canonical layer 는 c-light / l-light.
  // l-lightingline 은 의도적으로 매핑하지 않음 — 신규 import 는 layer_type_rules.py
  // 가 l-light 로 태깅하므로 더 이상 생성되지 않는다. 기존 데이터에 l-lightingline
  // 이 남아 있다면 l-light 로 치환 후 사용.
  [/^c-light/i, 'light'],
  [/^l-light/i, 'light'],
  [/^s-pillar/i, 'column'],
  [/^p-cctv/i, 'cctv'],
  [/^p-emergency/i, 'emergencyBell'],
  [/^p-sensor/i, 'sensor'],
  [/^p-speaker/i, 'sensor'],
]

/**
 * Standard layer names that route to known export categories.
 * Used by group-creation UI to guide users toward layer names that will
 * actually land in the right export bucket (cctvs, parkingLocations, lights, ...).
 *
 * Each entry: { name, category, description }
 *   - name: the canonical layer name to use
 *   - category: which routeLayerToCategory() bucket it lands in
 *   - description: human-friendly label for dropdown
 */
export const STANDARD_LAYER_NAMES: ReadonlyArray<{
  name: string
  category: string
  description: string
}> = [
  { name: 'c-cctv', category: 'cctv', description: 'CCTV' },
  { name: 'p-light-cctv', category: 'cctv', description: '조명 CCTV (DXF 표준)' },
  { name: 'p-warning-cctv', category: 'warningCctv', description: '단방향 CCTV (DXF 표준)' },
  { name: 'p-lightCctv', category: 'cctv', description: '조명 CCTV (placement)' },
  { name: 'p-warningCctv', category: 'warningCctv', description: '단방향 CCTV (placement)' },
  { name: 'c-carCharger', category: 'carCharger', description: '전기 충전기' },
  { name: 'c-emergencybell', category: 'emergencyBell', description: '비상벨 (Emergency Bell)' },
  { name: 'c-sensor', category: 'sensor', description: '센서 (Sensor)' },
  { name: 'c-onepassreader', category: 'onePassReader', description: 'One-Pass Reader' },
  { name: 'c-occupancylight', category: 'occupancyLight', description: '만차등 (Occupancy Light)' },
  { name: 'c-light', category: 'light', description: '조명 (Light)' },
  { name: 'l-light', category: 'light', description: '조명 라인 (Light - Line)' },
  { name: 'p-parking-basic', category: 'parkingLocation', description: '주차면 - 일반' },
  { name: 'p-parking-disabled', category: 'parkingLocation', description: '주차면 - 장애인' },
  { name: 'p-parking-electric', category: 'parkingLocation', description: '주차면 - 전기차' },
  { name: 'p-parking-compact', category: 'parkingLocation', description: '주차면 - 경차' },
  { name: 'p-guideboard', category: 'guideBoard', description: '안내판 (Guide Board)' },
  { name: 'e-zone', category: 'zone', description: 'Zone' },
  { name: 'e-pillar', category: 'column', description: '기둥 (Column / Pillar)' },
  { name: 'e-charger', category: 'charger', description: '충전기 (EV Charger)' },
  { name: 'e-carcharger', category: 'carCharger', description: '차량 충전기 (Car Charger)' },
  { name: 'e-elevator', category: 'elevator', description: '엘리베이터 (Elevator)' },
  { name: 'e-arrow', category: 'arrow', description: '화살표 (Arrow)' },
  { name: 'e-outline', category: 'outLine', description: '외곽선 (Out Line)' },
  { name: 'e-innerline', category: 'innerLine', description: '내부선 (Inner Line)' },
  { name: 'e-entrance', category: 'entrance', description: '출입구 (Entrance)' },
]

/**
 * Route object type or layer name to a normalized category.
 * Checks direct type mapping first, then layer prefix patterns.
 * Returns 'unknown' if no match.
 */
export function routeLayerToCategory(typeOrLayer: string): string {
  if (TYPE_MAPPING[typeOrLayer]) {
    return TYPE_MAPPING[typeOrLayer]
  }

  for (const [pattern, category] of LAYER_PATTERNS) {
    if (pattern.test(typeOrLayer)) {
      return category
    }
  }

  return 'unknown'
}

/**
 * Parking-location 표준 타입 (router/DB `parking_locations.type` 컬럼 값).
 *
 * router/src/enum/parking.enum.ts 의 ParkingType enum 과 1:1 정렬:
 *   REGULAR (일반) / COMPACT (경차) / HANDICAPPED (장애인) / ELECTRIC (전기차)
 *   FAMILY (가족배려) / WOMEN_ONLY (여성전용) / VULNERABLE (교통약자)
 *   MOTORCYCLE (이륜차)
 *
 * 주의: TS identifier `WOMENONLY` 의 string value 는 `WOMEN_ONLY` (underscore).
 */
export type ParkingTypeCode =
  | 'REGULAR'
  | 'COMPACT'
  | 'HANDICAPPED'
  | 'ELECTRIC'
  | 'FAMILY'
  | 'WOMEN_ONLY'
  | 'VULNERABLE'
  | 'MOTORCYCLE'

/**
 * 레이어 매핑 단계에서 사용자가 선택한 카테고리(예: `p-parking-disable`)나
 * DXF 블록명에 한글이 섞여 있는 케이스 (`#배치도_지하주차장$0$여성전용주차`)
 * 모두를 router/DB 가 기대하는 표준 type 코드로 변환.
 *
 * 매핑 규칙 (위에서 아래로 첫 매칭 적용):
 *   장애 / disable[d] / handicap         → HANDICAPPED (장애인)
 *   교통약자 / vulnerable                  → VULNERABLE  (교통약자)
 *   여성[전용] / women                     → WOMEN_ONLY  (여성전용)
 *   가족[배려] / family                    → FAMILY      (가족배려)
 *   이륜 / motorcycle                      → MOTORCYCLE  (이륜차)
 *   전기 / electric / ev                  → ELECTRIC    (전기차)
 *   경차 / 경형 / compact / small         → COMPACT     (경차)
 *   그 외 / 일반 / basic / 빈 값          → REGULAR     (일반)
 *
 * 사용자가 PropertyEditor 에서 명시적으로 다른 값을 선택하면 그 값이 우선
 * — 이 함수는 import 단계의 기본값 산출용.
 */
export function parkingTypeFromLayer(layer: string | undefined | null): ParkingTypeCode {
  if (!layer) return 'REGULAR'
  const lower = String(layer).toLowerCase()
  const raw = String(layer)

  // 장애인: '장애', 'disable[d]', 'handicap', 'p-parking-disable*'
  if (raw.includes('장애') || lower.includes('disable') || lower.includes('handicap')) {
    return 'HANDICAPPED'
  }
  // 교통약자: 한글이 '교통약자' 형태로 길어서 다른 키워드와 충돌하지 않음.
  if (raw.includes('교통약자') || lower.includes('vulnerable')) {
    return 'VULNERABLE'
  }
  // 여성전용
  if (raw.includes('여성') || lower.includes('women') || lower.includes('woman')) {
    return 'WOMEN_ONLY'
  }
  // 가족배려
  if (raw.includes('가족') || lower.includes('family')) {
    return 'FAMILY'
  }
  // 이륜차: 자동차 키워드보다 먼저 (motor 어간 충돌 방지)
  if (raw.includes('이륜') || raw.includes('오토바이') || lower.includes('motorcycle') || lower.includes('motor-bike')) {
    return 'MOTORCYCLE'
  }
  // 전기차
  if (raw.includes('전기') || raw.includes('충전') || lower.includes('electric') || /(?<![a-z])ev(?![a-z])/.test(lower)) {
    return 'ELECTRIC'
  }
  // 경차/소형
  if (raw.includes('경차') || raw.includes('경형') || raw.includes('소형') || lower.includes('compact') || lower.includes('parking-small') || lower.includes('small')) {
    return 'COMPACT'
  }
  // 일반 (명시적 키워드는 매칭 안 해도 fallback 으로 동작)
  return 'REGULAR'
}

/**
 * Resolve a layer/type to a template object type key.
 * First gets the normalized category via routeLayerToCategory,
 * then fuzzy-matches against template objectTypes keys.
 *
 * Example: layer "c-cctv" → category "cctv" → template key "lightCctv"
 * (because "lightCctv".toLowerCase().endsWith("cctv"))
 */
export function resolveLayerToTemplateKey(
  typeOrLayer: string,
  templateObjectTypes: Record<string, unknown>
): string | undefined {
  const category = routeLayerToCategory(typeOrLayer)
  if (category === 'unknown') return undefined

  // Direct match: category === template key
  if (templateObjectTypes[category]) return category

  // Plural-form match: e.g. category "light" → template key "lights".
  // 템플릿이 복수형 키를 쓰는 경우(`lights`, `arrows` 등)와
  // 카테고리 단수형(`light`, `arrow`) 간 불일치를 해소.
  if (templateObjectTypes[category + 's']) return category + 's'

  // Fuzzy match: find template key that ends with the category
  let bestMatch: string | undefined
  let bestScore = 0
  for (const tmplKey of Object.keys(templateObjectTypes)) {
    const lowerKey = tmplKey.toLowerCase()
    const lowerCategory = category.toLowerCase()
    if (lowerKey === lowerCategory) { bestMatch = tmplKey; break }
    if (lowerKey.endsWith(lowerCategory) && lowerCategory.length > bestScore) {
      bestMatch = tmplKey
      bestScore = lowerCategory.length
    }
  }

  return bestMatch
}
